import { Form, StandardFormButtons } from '@components/form';
import { useModal, type UseModalProps } from '@components/modal';
import { Typography } from '@components/typography';
import { useCreateVolumeMutation } from '@generated/graphql';
import { Stack } from '@mui/material';
import { useOrganizationContext } from '@navigation/OrganizationContext';
import { useNotifications } from '@notifications/Notifications';
import type { FormikHelpers } from 'formik';
import posthog from 'posthog-js';
import * as yup from 'yup';
import {
  VolumeDescriptionField,
  VolumeNameField,
  VolumeTagsField,
  volumeDescriptionSchema,
  volumeNameSchema,
  volumeTagsSchema,
} from './fields';

type CreateVolumeModalProps = {
  onCreate?: VoidFunction;
  onClose: () => void;
};

const validationSchema = yup.object({
  name: volumeNameSchema,
  description: volumeDescriptionSchema,
  tags: volumeTagsSchema,
});
type FormValues = yup.InferType<typeof validationSchema>;

const CreateVolumeModal = ({ onCreate, onClose }: CreateVolumeModalProps) => {
  const { organizationId } = useOrganizationContext();
  const { createAsyncNotification } = useNotifications();
  const [createVolumeMutation] = useCreateVolumeMutation();

  const createVolume = async (
    { name, description, tags }: FormValues,
    { setStatus }: FormikHelpers<FormValues>,
  ) => {
    posthog.capture('agent-studio:user_create-volume_submitted', { organizationId });
    await createVolumeMutation({
      variables: { organizationId, name, description, tags },
      onCompleted: () => {
        posthog.capture('agent-studio:user_create-volume_completed');
        onCreate?.();
        onClose();
      },
      onError: (error) => {
        posthog.capture('agent-studio:user_create-volume_error', { error: error.message });
        setStatus({ error: error.message });
        throw error;
      },
    });
  };

  const handleFormSubmit = createAsyncNotification('Volume created successfully', createVolume, {
    loadingMessage: 'Creating volume...',
  });

  return (
    <Form<FormValues>
      initialValues={{ name: '', description: '', tags: [] }}
      validationSchema={validationSchema}
      validateOnBlur={false}
      onSubmit={handleFormSubmit}
    >
      <Stack>
        <VolumeNameField />
        <VolumeDescriptionField />
        <VolumeTagsField />
        <StandardFormButtons onClose={onClose} submitLabel="Create" />
      </Stack>
    </Form>
  );
};

const NewVolumeModalHelpContent = (
  <Typography variant="body2">
    Volumes store static assets such as 3D models and backgrounds for use in synthetic data
    generation. Volumes created via the GUI are referred to as Workspace volumes. Workspace volumes
    are associated with user-managed workspaces and are created and maintained by users. These
    volumes are dynamically updated for increased fidelity of generated datasets.
  </Typography>
);

export const useCreateVolumeModal = ({
  componentProps,
  modalProps,
}: UseModalProps<typeof CreateVolumeModal>) => {
  return useModal({
    component: CreateVolumeModal,
    componentProps,
    modalProps: { title: 'Create new volume', help: NewVolumeModalHelpContent, ...modalProps },
  });
};
